import React, { useEffect, useState } from 'react';
import { adminApi } from '../api/admin';
import { useToast } from '../hooks/useToast';

type Company = {
  id: number; name: string; email: string | null; is_active: number | boolean;
  approval_status: string; plan: string; currency: string | null; created_at: string;
  admin_name: string | null; admin_email: string | null; modules: string[]; all_modules: string[];
};
type Sa = { id: number; name: string; email: string; company: string | null };
type Found = { id: number; name: string; email: string; is_super_admin: number | boolean; company: string | null };

const badge = (status: string) => {
  const map: Record<string, string> = { approved: 'var(--green)', pending: 'var(--blue)', rejected: 'var(--red)' };
  const c = map[status] || 'var(--tx3)';
  return <span style={{ fontSize: 11, fontWeight: 700, color: c, background: 'var(--sur2)', border: '1px solid var(--bdr)', borderRadius: 999, padding: '2px 9px', textTransform: 'capitalize' }}>{status}</span>;
};

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase', textAlign: 'left' };

const Tenants: React.FC = () => {
  const { showToast } = useToast();
  const [tab, setTab] = useState<'companies' | 'admins'>('companies');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, boolean>>({});

  const [admins, setAdmins] = useState<Sa[]>([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Found[]>([]);

  const load = () => {
    setLoading(true);
    adminApi.companies().then(r => setCompanies(r.data.data || [])).catch(() => showToast('Could not load companies', 'error')).finally(() => setLoading(false));
  };
  const loadAdmins = () => { adminApi.superAdmins().then(r => setAdmins(r.data.data || [])).catch(() => {}); };
  useEffect(() => { load(); loadAdmins(); }, []);

  const approve = (c: Company) => { setBusy(c.id); adminApi.approve(c.id).then(() => { showToast(c.name + ' approved.'); load(); }).catch((e) => showToast(e.response?.data?.message || 'Failed', 'error')).finally(() => setBusy(null)); };
  const reject  = (c: Company) => { if (!window.confirm('Reject ' + c.name + '? They will not be able to sign in.')) return; setBusy(c.id); adminApi.reject(c.id).then(() => { showToast(c.name + ' rejected.'); load(); }).catch((e) => showToast(e.response?.data?.message || 'Failed', 'error')).finally(() => setBusy(null)); };

  const openModules = (c: Company) => {
    if (editId === c.id) { setEditId(null); return; }
    const d: Record<string, boolean> = {};
    c.all_modules.forEach(m => { d[m] = c.modules.includes(m); });
    setDraft(d); setEditId(c.id);
  };
  const saveModules = (c: Company) => {
    const mods = Object.keys(draft).filter(m => draft[m]);
    setBusy(c.id);
    adminApi.setModules(c.id, mods).then(() => { showToast('Modules updated for ' + c.name); setEditId(null); load(); }).catch((e) => showToast(e.response?.data?.message || 'Failed', 'error')).finally(() => setBusy(null));
  };

  const doSearch = (val: string) => {
    setQ(val);
    if (val.trim().length < 2) { setFound([]); return; }
    adminApi.searchUsers(val).then(r => setFound(r.data.data || [])).catch(() => setFound([]));
  };
  const toggleAdmin = (id: number, value: boolean) => {
    adminApi.setSuperAdmin(id, value).then(r => { showToast(r.data.message || 'Updated'); loadAdmins(); if (q) doSearch(q); }).catch((e) => showToast(e.response?.data?.message || 'Failed', 'error'));
  };

  const pending = companies.filter(c => c.approval_status === 'pending');
  const rest = companies.filter(c => c.approval_status !== 'pending');

  const row = (c: Company) => (
    <React.Fragment key={c.id}>
      <tr style={{ borderBottom: '1px solid var(--bdr)' }}>
        <td style={{ padding: '10px' }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
          <div style={{ fontSize: 11, color: 'var(--tx3)' }}>{c.admin_email || c.email || '—'} · {c.currency || ''}</div>
        </td>
        <td style={{ padding: '10px' }}>{badge(c.approval_status)}</td>
        <td style={{ padding: '10px', fontSize: 12 }}>{c.modules.length}/{c.all_modules.length}</td>
        <td style={{ padding: '10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
          {c.approval_status !== 'approved' && <button className="btn btn-success btn-sm" disabled={busy === c.id} onClick={() => approve(c)}>Approve</button>}
          {c.approval_status !== 'rejected' && <button className="btn btn-secondary btn-sm" disabled={busy === c.id} style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => reject(c)}>Reject</button>}
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => openModules(c)}>{editId === c.id ? 'Close' : 'Modules'}</button>
        </td>
      </tr>
      {editId === c.id && (
        <tr>
          <td colSpan={4} style={{ padding: '14px', background: 'var(--sur2)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>Modules for {c.name}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 8 }}>
              {c.all_modules.map(m => (
                <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textTransform: 'capitalize', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!draft[m]} onChange={e => setDraft(d => ({ ...d, [m]: e.target.checked }))} />
                  {m.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <button className="btn btn-primary btn-sm" disabled={busy === c.id} onClick={() => saveModules(c)}>Save modules</button>
              <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6 }} onClick={() => setEditId(null)}>Cancel</button>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Tenants</h1>
        <div style={{ fontSize: 12, color: 'var(--tx3)' }}>Approve companies, control their modules, and manage who can do this.</div>
      </div>

      <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
        <button className={`btn btn-sm ${tab === 'companies' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('companies')}>Companies</button>
        <button className={`btn btn-sm ${tab === 'admins' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('admins')}>Super Admins</button>
      </div>

      {tab === 'companies' && (
        <div className="card">
          {loading ? <div style={{ padding: 20, color: 'var(--tx3)' }}>Loading…</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--sur2)' }}><th style={th}>Company</th><th style={th}>Status</th><th style={th}>Modules</th><th style={{ ...th, textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {pending.length > 0 && <tr><td colSpan={4} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase' }}>Pending approval ({pending.length})</td></tr>}
                {pending.map(row)}
                {rest.length > 0 && <tr><td colSpan={4} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--tx3)', textTransform: 'uppercase' }}>All companies</td></tr>}
                {rest.map(row)}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'admins' && (
        <div className="card" style={{ padding: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Super Admin group</div>
          <div style={{ fontSize: 12, color: 'var(--tx3)', marginBottom: 14 }}>Members can open this console and approve companies. Add staff below.</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <tbody>
              {admins.map(a => (
                <tr key={a.id} style={{ borderBottom: '1px solid var(--bdr)' }}>
                  <td style={{ padding: '8px 10px' }}><b style={{ fontSize: 13 }}>{a.name}</b><div style={{ fontSize: 11, color: 'var(--tx3)' }}>{a.email} · {a.company}</div></td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}><button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }} onClick={() => toggleAdmin(a.id, false)}>Remove</button></td>
                </tr>
              ))}
              {admins.length === 0 && <tr><td style={{ padding: 10, color: 'var(--tx3)', fontSize: 12 }}>No super admins yet.</td></tr>}
            </tbody>
          </table>

          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Add a user</div>
          <input className="form-control" placeholder="Search by name or email…" value={q} onChange={e => doSearch(e.target.value)} style={{ maxWidth: 360 }} />
          {found.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
              <tbody>
                {found.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid var(--bdr)' }}>
                    <td style={{ padding: '8px 10px' }}><b style={{ fontSize: 13 }}>{u.name}</b><div style={{ fontSize: 11, color: 'var(--tx3)' }}>{u.email} · {u.company}</div></td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {u.is_super_admin ? <span style={{ fontSize: 11, color: 'var(--green)' }}>Already a super admin</span>
                        : <button className="btn btn-primary btn-sm" onClick={() => toggleAdmin(u.id, true)}>Add to group</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Tenants;
